import { useState } from "react";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { post } from "@common/api/apiShared";
import { TransferSelectedResponsibilitiesModel, UserResponsibilityDetailRowModel, UserResponsibilityDetailViewModel } from "../ReviewUserResponsibility.type";
import { IReviewUserResponsibilityDetailDialogProps } from "./ReviewUserResponsibilityDetailDialog";
import { useResponsibleQuery } from "../../hooks/useResponsibleQuery";
import { useTranslation } from "../../../localization/useTranslation";
import { EntityType } from "@common/Common.types";

export default function useReviewUserResponsibilityDetailDialog(props: IReviewUserResponsibilityDetailDialogProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString, translateUserResponsibilityPropertyType, translateDate, translateEntityType } = useTranslation();
    const { isLoading, data: apiData } = useDotLegalQuery<Array<UserResponsibilityDetailViewModel>>(
        `/users/${props.userId}/responsibilityDetails/${props.entityType}`
    );

    const data: Array<UserResponsibilityDetailRowModel> | undefined = apiData?.map((row) => ({
        ...row,
        propertyTypeName: row.propertyType !== null ? translateUserResponsibilityPropertyType(row.propertyType) : row.customFieldName,
        deadlineName: row.deadline ? translateDate(new Date(row.deadline)) : null,
        groupEntityName: row.groupEntity === null ? translateString("entireGroup") : row.groupEntity,
        riskEntityTypeName: row.riskEntityType !== null ? translateEntityType(EntityType[row.riskEntityType]) : null,
    }));

    const [toUser, setToUser] = useState<string | null>(null);

    const responsiblesQuery = useResponsibleQuery();

    const fromUsers = responsiblesQuery.userData ? responsiblesQuery.data(props.userId)?.filter((x) => x.id !== toUser) : undefined;
    const toUsers = responsiblesQuery.userData ? responsiblesQuery.data(toUser)?.filter((x) => x.id !== props.userId) : undefined;

    const [selectedEntities, setSelectedEntities] = useState<Set<string>>(new Set());
    const [searchString, setSearchString] = useState("");

    const getRowKey = (row: UserResponsibilityDetailViewModel) => `${row.entityId}-${row.propertyType ?? row.customFieldId}`;

    const toggleRowSelection = (row: UserResponsibilityDetailViewModel) => {
        const key = getRowKey(row);
        setSelectedEntities((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const matchesSearch = (row: UserResponsibilityDetailRowModel, search: string) => {
        const term = search.trim().toLowerCase();

        if (!term) {
            return true;
        }

        return Object.values(row).some((value) =>
            String(value ?? "")
                .toLowerCase()
                .includes(term)
        );
    };

    const visibleData = (data ?? []).filter((row) => matchesSearch(row, searchString));
    const visibleSelectedCount = visibleData.filter((row) => selectedEntities.has(getRowKey(row))).length;
    const headerCheckBoxState: "true" | "false" | "indeterminate" =
        visibleData.length > 0 && visibleSelectedCount === visibleData.length ? "true" : visibleSelectedCount > 0 ? "indeterminate" : "false";

    const onSelectAllRows = (selected: Array<UserResponsibilityDetailRowModel>) => {
        setSelectedEntities((prev) => {
            const next = new Set(prev);
            if (selected.length > 0) {
                selected.forEach((row) => next.add(getRowKey(row)));
            } else {
                visibleData.forEach((row) => next.delete(getRowKey(row)));
            }
            return next;
        });
    };

    const isRowSelected = (row: UserResponsibilityDetailViewModel) => selectedEntities.has(getRowKey(row));

    const onToUserChange = (value: string | null) => {
        setToUser(value);
    };

    function save(model: TransferSelectedResponsibilitiesModel) {
        return post<{}>("/users/transfer-selected-responsibilities", model);
    }

    const saveMutation = useDotLegalMutation(save, {
        onSuccess: () => {
            snackbar.show(translateString("responsibilityTransfered"));
            props.onSaved();
        },
    });

    const transferSelectedResponsibilities = async () => {
        if (!toUser || !data) return;

        const model: TransferSelectedResponsibilitiesModel = {
            fromUserId: props.userId,
            toUserId: toUser,
            entityType: props.entityType,
            entities: data
                .filter((row) => selectedEntities.has(getRowKey(row)))
                .map((row) => ({ entityId: row.entityId, propertyType: row.propertyType, customFieldId: row.customFieldId })),
        };

        await saveMutation.mutateAsync(model);
    };

    const selectedCount = selectedEntities.size;
    const transferButtonEnabled = toUser !== null && selectedCount > 0;

    return {
        isLoading,
        data,
        toUser,
        fromUsers,
        toUsers,
        onToUserChange,
        toggleRowSelection,
        onSelectAllRows,
        isRowSelected,
        headerCheckBoxState,
        setSearchString,
        selectedCount,
        transferSelectedResponsibilities,
        transferButtonEnabled,
        inProgress: saveMutation.isLoading,
    };
}
